import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static values = { realEstateAdminId: String, conversationAgentId: String }
  static targets = ['templatesList']

  connect() {
    this.selectedTemplate = null
    this.selectedTemplateData = null
    this.templatesLoaded = false

    // Load templates when controller connects (modal is opened)
    setTimeout(() => {
      if (!this.templatesLoaded) {
        this.loadAllTemplateDetails()
      }
    }, 100)
  }

  closeModal() {
    // Find the modal element that contains this controller
    const modal = this.element.closest('dialog')
    if (modal) {
      modal.close()
      // Reset selection when closing modal
      this.selectedTemplate = null
      this.resetTemplateSelection()
    }
  }

  selectTemplate(event) {
    // Remove selection from all templates
    const allTemplates = document.querySelectorAll('.template-option')
    allTemplates.forEach((template) => {
      template.classList.remove('bg-blue-50', 'border-blue-300')
      const radio = template.querySelector('input[type="radio"]')
      if (radio) radio.checked = false
    })

    // Add selection to clicked template
    const selectedTemplate = event.currentTarget
    selectedTemplate.classList.add('bg-blue-50', 'border-blue-300')

    // Check the radio button
    const radio = selectedTemplate.querySelector('input[type="radio"]')
    if (radio) {
      radio.checked = true
      this.selectedTemplate = radio.value
    }
  }

  resetTemplateSelection() {
    const allTemplates = document.querySelectorAll('.template-option')
    allTemplates.forEach((template) => {
      template.classList.remove('bg-blue-50', 'border-blue-300')
      const radio = template.querySelector('input[type="radio"]')
      if (radio) radio.checked = false
    })
  }

  confirmTemplate() {
    if (!this.selectedTemplate) {
      alert('Por favor, selecione um template antes de continuar.')
      return
    }

    // Store selected template ID for CSV upload
    window.selectedMassiveEngagementTemplate = this.selectedTemplate

    // Close template selector modal
    this.closeModal()

    // Show confirmation and proceed to CSV upload
    const csvUploadArea = document.getElementById('csv-upload-area')
    if (csvUploadArea) {
      csvUploadArea.scrollIntoView({ behavior: 'smooth' })

      // Show template selection confirmation
      this.showTemplateConfirmation()
    }
  }

  showTemplateConfirmation() {
    // Get template name and body from selected option
    const selectedOption = document.querySelector(`[data-template-id="${this.selectedTemplate}"]`)
    const templateName = selectedOption
      ? selectedOption.querySelector('.template-name').textContent
      : 'Template selecionado'
    const templateBody = selectedOption
      ? selectedOption.querySelector('.template-body').textContent
      : 'Template selecionado'

    // Update the template display in the modal
    const templateDisplay = document.getElementById('selected-template-display')
    const templateNameElement = document.getElementById('selected-template-name')
    const templateBodyElement = document.getElementById('selected-template-body')

    if (templateDisplay && templateNameElement && templateBodyElement) {
      templateNameElement.textContent = templateName
      templateBodyElement.textContent = templateBody
      templateDisplay.classList.remove('hidden')
    }

    // Dispatch event to notify the massive engagement controller
    const event = new CustomEvent('massiveEngagementTemplateSelected', {
      detail: {
        templateId: this.selectedTemplate,
        templateName: templateName,
        templateBody: templateBody,
      },
    })
    window.dispatchEvent(event)
  }

  async loadAllTemplateDetails() {
    const templateOptions = document.querySelectorAll('.template-option')

    for (const templateOption of templateOptions) {
      const templateId = templateOption.dataset.templateId
      const nameElement = templateOption.querySelector('.template-name')
      const bodyElement = templateOption.querySelector('.template-body')

      try {
        const templateData = await this.fetchTemplateDetails(templateId)

        if (templateData.success && templateData.template) {
          // Prefer the local friendly_name (display_name) over the raw Meta name
          let template_name = templateData.friendly_name || templateData.template.name
          if (template_name === 'massive_engagement_message') {
            template_name = 'Mensagem de Disparo Padrão'
          }

          nameElement.textContent = template_name

          // Find the body component and extract text
          let bodyText = 'Sem texto disponível'
          let variablesInfo = ''
          let headerInfo = ''

          if (templateData.template.components) {
            const bodyComponent = templateData.template.components.find(
              (comp) => comp.type === 'BODY'
            )
            if (bodyComponent && bodyComponent.text) {
              bodyText = bodyComponent.text

              // Extract variables from template text
              const variables = bodyText.match(/\{\{(\d+)\}\}/g)
              if (variables && variables.length > 0) {
                const uniqueVars = [...new Set(variables)].sort()
                variablesInfo = ` (Requer: ${uniqueVars.join(', ')})`
              }

              // Truncate if too long
              if (bodyText.length > 100) {
                bodyText = bodyText.substring(0, 100) + '...'
              }
            }

            const headerComponent = templateData.template.components.find(
              (comp) => comp.type === 'HEADER'
            )
            if (headerComponent && headerComponent.format === 'IMAGE') {
              headerInfo = ' [Cabeçalho: imagem]'
            }
          }

          bodyElement.textContent = bodyText + variablesInfo + headerInfo
          bodyElement.classList.remove('text-gray-600')
          bodyElement.classList.add('text-gray-700')
        } else {
          nameElement.textContent = templateId
          bodyElement.textContent = 'Erro ao carregar template'
          bodyElement.classList.add('text-red-600')
        }
      } catch (error) {
        console.error('Error loading template:', templateId, error)
        nameElement.textContent = templateId
        bodyElement.textContent = 'Erro de conexão'
        bodyElement.classList.add('text-red-600')
      }
    }

    this.templatesLoaded = true
  }

  async fetchTemplateDetails(templateId) {
    try {
      // Use the real estate admin ID from data attribute
      if (!this.realEstateAdminIdValue) {
        throw new Error('Real Estate Admin ID not available')
      }

      if (!this.conversationAgentIdValue) {
        throw new Error('Conversation Agent ID not available')
      }

      const response = await fetch(
        `/real_estate_admin/${this.realEstateAdminIdValue}/whatsapp_templates/template_details`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('[name="csrf-token"]').getAttribute('content'),
          },
          body: JSON.stringify({
            template_id: templateId,
            conversation_agent_id: this.conversationAgentIdValue,
          }),
        }
      )

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      console.error('Fetch error:', error)
      throw error
    }
  }
}
